import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Send, MessageSquare, Mail, Phone, MapPin, Globe, Rocket, Brain, Target, Shield, Database, Cpu, ChevronRight, Sparkles, Zap, Lock } from 'lucide-react'

const Slide8Interface = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: false
  })

  const [showChatbot, setShowChatbot] = useState(false)
  const [budget, setBudget] = useState('')
  const [chatMessages, setChatMessages] = useState([
    { id: 1, sender: 'bot', text: 'Hello! I am Q, your AI assistant. What can I help you build today?' }
  ])
  const [userMessage, setUserMessage] = useState('')
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [activeTab, setActiveTab] = useState('human') // Changed to 'human' first
  const messagesEndRef = useRef(null)

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [chatMessages])

  const handleContactChange = (e) => {
    const { name, value } = e.target
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateContactForm = () => {
    const errors = {}
    
    if (!contactForm.name.trim()) errors.name = 'Name is required'
    if (!contactForm.email.trim()) errors.email = 'Email is required'
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(contactForm.email)) {
      errors.email = 'Invalid email address'
    }
    if (!contactForm.message.trim()) errors.message = 'Message is required'
    if (!budget) errors.budget = 'Please select a budget range'
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const onSubmitContact = (e) => {
    e.preventDefault()
    
    if (!validateContactForm()) {
      return
    }

    try {
      console.log('Contact form submitted:', { ...contactForm, budget })
      
      // Show success message
      alert('Trajectory launched! Our team will contact you within 24 hours.')
      
      // Reset form
      setContactForm({
        name: '',
        email: '',
        company: '',
        message: ''
      })
      setBudget('')
      setFormErrors({})
    } catch (error) {
      alert('Launch failed. Please try again.')
    }
  }

  const handleSendMessage = () => {
    if (!userMessage.trim()) return

    const newUserMessage = {
      id: chatMessages.length + 1,
      sender: 'user',
      text: userMessage
    }

    setChatMessages(prev => [...prev, newUserMessage])
    setUserMessage('')

    // Simulate AI response
    setTimeout(() => {
      const responses = [
        "I understand you're interested in our services. Let me connect you with the right team.",
        "Based on your query, I recommend our Growth Reactor for marketing needs.",
        "For technical projects, our Digital Cortex team would be perfect. Shall I connect you?",
        "I'm processing your request and will provide a project scope shortly.",
        "Thank you for sharing your requirements. Our team will review this and get back to you."
      ]
      
      const botResponse = {
        id: chatMessages.length + 2,
        sender: 'bot',
        text: responses[Math.floor(Math.random() * responses.length)]
      }

      setChatMessages(prev => [...prev, botResponse])
    }, 1000)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    hover: {
      y: -4,
      scale: 1.01,
      transition: { duration: 0.2 }
    }
  }

  // Floating background elements
  const FloatingOrb = ({ delay = 0 }) => (
    <motion.div
      className="absolute rounded-full"
      animate={{
        y: [0, -20, 0],
        opacity: [0.1, 0.3, 0.1],
      }}
      transition={{
        duration: 4,
        delay,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  )

  return (
    <div id="interface" className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-gray-950 via-black to-gray-950" ref={ref}>
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(56, 189, 248, 0.03) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(56, 189, 248, 0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Floating Orbs */}
        <FloatingOrb className="w-64 h-64 bg-cyan-500/5 -top-32 -left-32 blur-3xl" />
        <FloatingOrb className="w-96 h-96 bg-purple-500/5 -bottom-48 -right-48 blur-3xl" delay={1} />
        <FloatingOrb className="w-48 h-48 bg-blue-500/5 top-1/4 right-1/3 blur-3xl" delay={2} />
        
        {/* Animated Nodes */}
        {[...Array(8)].map((_, i) => {
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          const size = 1 + Math.random() * 2;

          return (
            <motion.div
              key={i}
              className="absolute rounded-full bg-gradient-to-r from-cyan-500/30 to-blue-500/30"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${size}px`,
                height: `${size}px`,
              }}
              animate={{
                opacity: [0.1, 0.8, 0.1],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 2 + Math.random(),
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          );
        })}
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-28">
        {/* Interface Toggle for Mobile */}
        <div className="lg:hidden mb-8">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => setActiveTab('human')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all ${
                activeTab === 'human'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                  : 'bg-gray-800/50 text-gray-400 border border-gray-700'
              }`}
            >
              <Send className="w-4 h-4" />
              <span>Contact Form</span>
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all ${
                activeTab === 'ai'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                  : 'bg-gray-800/50 text-gray-400 border border-gray-700'
              }`}
            >
              <Brain className="w-4 h-4" />
              <span>AI Assistant</span>
            </button>
          </div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="relative max-w-7xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center justify-center space-x-3 mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-full"></div>
                <Rocket className="relative w-8 h-8 md:w-12 md:h-12 text-cyan-400" />
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                  PROJECT INTERFACE
                </span>
              </h1>
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-full"></div>
                <Target className="relative w-8 h-8 md:w-12 md:h-12 text-cyan-400" />
              </div>
            </div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-400 font-light max-w-3xl mx-auto px-4">
              Connect with our intelligence systems. Choose your preferred interface for project initiation.
            </p>
          </motion.div>

          {/* Main Interface Grid - SWAPPED ORDER */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 mb-12 md:mb-16">
            {/* Contact Form Card - FIRST */}
            <motion.div
              variants={cardVariants}
              whileHover="hover"
              className={`relative transition-all duration-300 ${
                activeTab === 'human' ? 'block' : 'hidden lg:block'
              }`}
            >
              <div className="h-full rounded-2xl overflow-hidden border border-gray-800 bg-gradient-to-br from-gray-900/80 to-gray-900/40 backdrop-blur-sm">
                <div className="relative p-6 md:p-8">
                  {/* Glow Effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur opacity-0 group-hover:opacity-70 transition duration-1000"></div>
                  
                  {/* Card Content */}
                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500 blur-lg opacity-50"></div>
                          <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                            <Send className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold text-white">
                            PROJECT REQUEST
                          </h3>
                          <p className="text-sm text-blue-400/80">Human Connection • Secure</p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 px-3 py-1 bg-white/5 rounded-full">
                        24h Response
                      </div>
                    </div>

                    <form onSubmit={onSubmitContact} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-400">
                            Your Name
                          </label>
                          <input
                            name="name"
                            value={contactForm.name}
                            onChange={handleContactChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                            placeholder="Enter your name"
                          />
                          {formErrors.name && (
                            <p className="text-red-400 text-sm mt-1">{formErrors.name}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-400">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={contactForm.email}
                            onChange={handleContactChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                            placeholder="you@company.com"
                          />
                          {formErrors.email && (
                            <p className="text-red-400 text-sm mt-1">{formErrors.email}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-400">
                          Company / Organization
                        </label>
                        <input
                          name="company"
                          value={contactForm.company}
                          onChange={handleContactChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                          placeholder="Your organization name"
                        />
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-400">
                          Project Budget Range
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {[
                            { value: 'startup', label: '< $10k', desc: 'Startup' },
                            { value: 'growth', label: '$10k - $50k', desc: 'Growth' },
                            { value: 'enterprise', label: '$50k+', desc: 'Enterprise' }
                          ].map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setBudget(option.value)}
                              className={`p-4 rounded-xl border transition-all duration-300 text-left ${
                                budget === option.value
                                  ? 'border-cyan-500/50 bg-gradient-to-br from-cyan-500/10 to-blue-500/10'
                                  : 'border-white/10 bg-white/5 hover:border-cyan-500/30 hover:bg-white/10'
                              }`}
                            >
                              <div className="text-lg font-semibold text-white">
                                {option.label}
                              </div>
                              <div className="text-sm text-gray-400">
                                {option.desc}
                              </div>
                            </button>
                          ))}
                        </div>
                        {formErrors.budget && (
                          <p className="text-red-400 text-sm mt-1">{formErrors.budget}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-400">
                          Project Requirements
                        </label>
                        <textarea
                          name="message"
                          value={contactForm.message}
                          onChange={handleContactChange}
                          rows="4"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all resize-none"
                          placeholder="Describe your project goals, timeline, and specific requirements..."
                        />
                        {formErrors.message && (
                          <p className="text-red-400 text-sm mt-1">{formErrors.message}</p>
                        )}
                      </div>

                      <button
                        type="submit"
                        className="group w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center space-x-2"
                      >
                        <Rocket className="w-5 h-5" />
                        <span>LAUNCH PROJECT REQUEST</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* AI Chatbot Card - SECOND */}
            <motion.div
              variants={cardVariants}
              whileHover="hover"
              className={`relative transition-all duration-300 ${
                activeTab === 'ai' ? 'block' : 'hidden lg:block'
              }`}
            >
              <div className="h-full rounded-2xl overflow-hidden border border-gray-800 bg-gradient-to-br from-gray-900/80 to-gray-900/40 backdrop-blur-sm">
                <div className="relative p-6 md:p-8">
                  {/* Glow Effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur opacity-0 group-hover:opacity-70 transition duration-1000"></div>
                  
                  {/* Card Content */}
                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-500 blur-lg opacity-50"></div>
                          <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                            <Brain className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold text-white">
                            Q AI ASSISTANT
                          </h3>
                          <p className="text-sm text-cyan-400/80">Intelligence Core • Online</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-green-400">Live</span>
                      </div>
                    </div>

                    <p className="text-gray-300 mb-8 leading-relaxed">
                      Experience instant project scoping with our AI assistant. Get immediate responses, 
                      technical recommendations, and preliminary estimates without waiting.
                    </p>

                    <div className="space-y-4 mb-8">
                      {[
                        { icon: Zap, text: 'Instant Response', color: 'text-yellow-400' },
                        { icon: Lock, text: 'Secure & Encrypted', color: 'text-green-400' },
                        { icon: Sparkles, text: 'Smart Recommendations', color: 'text-purple-400' },
                        { icon: Cpu, text: '24/7 Availability', color: 'text-cyan-400' }
                      ].map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center ${feature.color}`}>
                            <feature.icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm text-gray-300">{feature.text}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => setShowChatbot(true)}
                      className="group w-full px-6 py-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 text-white font-semibold rounded-xl hover:from-cyan-500/20 hover:to-blue-500/20 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <MessageSquare className="w-5 h-5" />
                      <span>OPEN SECURE CHAT</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Global Coordinates */}
          <motion.div variants={itemVariants} className="mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-gray-900/50 to-gray-900/20 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-6 md:p-8">
              <div className="flex items-center justify-center space-x-3 mb-8">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-cyan-400" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  GLOBAL PRESENCE
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                    <MapPin className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-3">HEADQUARTERS</h4>
                  <div className="text-gray-300 space-y-1 text-sm">
                    <p>Quavrion Technologies</p>
                    <p>Hyderabad, Telangana 500032</p>
                    <p>India</p>
                    <p className="text-cyan-400/80 mt-2">+91 98765 43210</p>
                  </div>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                    <Mail className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-3">CONTACT</h4>
                  <div className="text-gray-300 space-y-2 text-sm">
                    <div>
                      <p className="text-cyan-400/80">General Inquiries</p>
                      <p>hello@quavrion.com</p>
                    </div>
                    <div>
                      <p className="text-cyan-400/80">Business Development</p>
                      <p>growth@quavrion.com</p>
                    </div>
                    <div>
                      <p className="text-cyan-400/80">Support</p>
                      <p>support@quavrion.com</p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                    <Globe className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-3">OPERATIONS</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-2xl mb-1">🇮🇳</div>
                      <p className="text-gray-300">India</p>
                      <p className="text-cyan-400/80 text-xs">HQ</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-2xl mb-1">🇦🇪</div>
                      <p className="text-gray-300">UAE</p>
                      <p className="text-cyan-400/80 text-xs">Dubai</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-2xl mb-1">🇺🇸</div>
                      <p className="text-gray-300">USA</p>
                      <p className="text-cyan-400/80 text-xs">Remote</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="text-2xl mb-1">🌍</div>
                      <p className="text-gray-300">Global</p>
                      <p className="text-cyan-400/80 text-xs">Remote</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="text-center">
            <div className="relative p-8 md:p-10 rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-900/20 border border-gray-800 backdrop-blur-sm">
              <div className="absolute top-4 right-4">
                <Sparkles className="w-6 h-6 text-cyan-400/50" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                READY TO INNOVATE?
              </h3>
              <p className="text-gray-400 mb-8 max-w-2xl mx-auto text-lg">
                Choose your interface. Both lead to the same destination: building something extraordinary together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => {
                    const contactForm = document.getElementById('interface');
                    if (contactForm) {
                      contactForm.scrollIntoView({ behavior: 'smooth' });
                      setActiveTab('human');
                    }
                  }}
                  className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <Send className="w-5 h-5" />
                  <span>GO TO CONTACT FORM</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button 
                  onClick={() => setShowChatbot(true)}
                  className="px-8 py-4 bg-transparent border-2 border-cyan-500/30 text-cyan-300 font-semibold rounded-xl hover:border-cyan-300 hover:text-cyan-200 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <Brain className="w-5 h-5" />
                  <span>START WITH AI</span>
                </button>
              </div>
              <p className="text-gray-500 text-sm mt-6">
                Average response time: <span className="text-blue-400">Human: 24 hours</span> • <span className="text-cyan-400">AI: Instant</span>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Chatbot Modal */}
      {showChatbot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={() => setShowChatbot(false)}
          />
          
          {/* Chatbot Container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className="relative w-full max-w-2xl bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-gray-800 shadow-2xl shadow-cyan-500/10 overflow-hidden"
          >
            {/* Chatbot Header */}
            <div className="p-6 border-b border-gray-800 bg-gradient-to-r from-cyan-500/10 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-500 blur-lg opacity-50"></div>
                    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      Q AI ASSISTANT
                    </h3>
                    <p className="text-xs text-cyan-400">Secure Channel • End-to-End Encrypted</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowChatbot(false)}
                    className="p-2 text-gray-400 hover:text-white transition-colors hover:bg-white/5 rounded-lg"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="h-80 md:h-96 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-gray-900/50 to-transparent">
              {chatMessages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs md:max-w-md rounded-2xl p-4 ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-br-none'
                        : 'bg-gray-800/50 text-gray-300 rounded-bl-none border border-gray-700/50'
                    }`}
                  >
                    <p className="text-sm md:text-base">{message.text}</p>
                    <div className={`text-xs mt-2 flex items-center space-x-1 ${
                      message.sender === 'user' ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      {message.sender === 'bot' && <Lock className="w-3 h-3" />}
                      <span>{message.sender === 'bot' ? 'Q • Secure' : 'You'}</span>
                      <span>•</span>
                      <span>{message.sender === 'bot' ? 'AI Assistant' : 'Just now'}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-6 border-t border-gray-800 bg-gradient-to-t from-gray-900/50 to-transparent">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message here..."
                  className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!userMessage.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>SEND</span>
                </button>
              </div>
              <div className="flex items-center justify-center space-x-2 mt-3">
                <Lock className="w-3 h-3 text-green-400" />
                <p className="text-xs text-gray-500">
                  Secure connection • Your data is encrypted and protected
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Slide8Interface